import AdminRequired from 'admin/adminRequired'
import Permission from 'admin/permissionRequired'
import { User } from 'auth/state'
import classNames from 'classnames'
import Feature from 'features/feature'
import { LoggingService } from 'http/loggingService'
import { ORDER_PATHS } from 'orders/paths'
import * as React from 'react'
import { useSelector } from 'react-redux'
import { useListSupersetReports } from 'reports/superset/hooks'
import MenuItem from 'sidebar/sidebarMenuItem'
import { pathUtils } from 'utils/pathnameFormatter'
import { useLazyListOrdersQuery } from '../orders/reduxQueries'
import { isSidebarOpen } from './selectors'
import { AccountSelector } from './sidebarAccountSelector'
import { LanguageSelector } from './languageSelector'
import { RELEASED_BADGE } from 'releaseNotes/htmlComponents'
import { AppServices } from 'middleware'
import './styles.scss'
import { useAppSelector } from 'store/hooks'
import { NotificationsServiceContext } from 'settings/context'
import { GlobalSearchTrigger } from 'globalSearch/globalSearchTrigger'
import { usePulseAccess } from 'pulse/hooks/usePulseAccess'
import { useTranslation } from 'translations'

interface SidebarProps {
    accountSlug: string
    selfSignUp: string | null
    hasPermission: (permission: string, accountSlug: string) => boolean
    hasFeature: (feature: string, accountSlug?: string) => boolean
    loggingService: LoggingService
    user: User | null
}

export function SidebarMenu(props: Readonly<SidebarProps>) {
    const { accountSlug, selfSignUp, hasPermission, hasFeature, loggingService, user } = props
    const { t } = useTranslation()
    const showProducts =
        (hasPermission('view_pricing_settings', accountSlug) || hasPermission('view_product_validity', accountSlug)) &&
        !props.user?.resellerId
    const isReseller = !!props.user?.resellerId
    const showChannelsManagerPage =
        hasFeature('ResellersFeature', accountSlug) ||
        hasFeature('RetailersReservationCodes', accountSlug) ||
        hasFeature('GoogleThingsToDo', accountSlug)
    const pulseEnabled = usePulseAccess(accountSlug)
    const [openItem, setOpenItem] = React.useState<string>('')
    const open = useSelector(isSidebarOpen)
    const supersetReports = useListSupersetReports()
    const [_, status] = useLazyListOrdersQuery()
    const isNewCartPageEnabled = hasFeature('show_new_cart_page', accountSlug)
    const profile = useAppSelector((state) => state.profile.profile)
    const notificationsService = React.useContext(NotificationsServiceContext)
    const [newNotificationsCount, setNewNotificationsCount] = React.useState<number>(0)

    function logClick(path: string) {
        if (path === 'bug_report') {
            loggingService.logAction('bug_report')
        } else {
            loggingService.logAction('navigation', {
                click_to: path,
                source: 'sidebar',
            })
        }
    }

    function clickChildless(path: string) {
        logClick(path)
        setOpenItem(path)
    }

    function logout() {
        AppServices.loginService.logout()
    }

    React.useEffect(() => {
        notificationsService.getNewNotificationsCount(accountSlug).then((count) => {
            setNewNotificationsCount(count)
        })
    }, [accountSlug])

    React.useEffect(() => {
        if (/\/feed\b/.test(window.location.pathname)) {
            setNewNotificationsCount(0)
        }
    }, [window.location.pathname])

    return (
        <div
            className={classNames('sidebar-wrapper', {
                'with-impersonation-banner': !!user?.impersonatedBy,
                open: open,
            })}
        >
            <div className="account-selector">
                <AccountSelector selfSignUp={selfSignUp} />
            </div>
            <Feature name="globalSearch" accountSlug={accountSlug}>
                <div className="search-trigger-wrapper">
                    <GlobalSearchTrigger />
                </div>
            </Feature>
            <nav className="main-navigation" aria-label={t('sidebar.mainNavigation')}>
                <ul className="menu">
                    <MenuItem
                        path={`/account/${accountSlug}/dashboard/overview/`}
                        text={pulseEnabled ? t('sidebar.menu.pulse') : t('sidebar.menu.dashboard')}
                        onClick={clickChildless}
                        iconName={pulseEnabled ? 'Activity' : 'Home'}
                    />
                    <Permission name="access_orders" accountSlug={accountSlug}>
                        <MenuItem
                            path={`/account/${accountSlug}/orders/`}
                            text={t('sidebar.menu.orders')}
                            iconName="ShoppingBag"
                            onClick={setOpenItem}
                            close={openItem !== `/account/${accountSlug}/orders/`}
                        >
                            <Feature name="oldOrdersPages" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/orders/transactions/`}
                                    text={t('sidebar.menu.orders')}
                                    onClick={logClick}
                                    floatText="OLD"
                                />
                                <MenuItem
                                    path={`/account/${accountSlug}/orders/details/`}
                                    text={t('sidebar.menu.ordersDetails')}
                                    onClick={logClick}
                                    floatText="OLD"
                                />
                            </Feature>
                            <MenuItem
                                path={`/account/${accountSlug}/orders/orders/`}
                                text={t('sidebar.menu.orderList')}
                                onClick={logClick}
                            />
                            <Feature name="dailySchedule" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/orders/daily_schedule/`}
                                    text={t('sidebar.menu.dailySchedule')}
                                    onClick={logClick}
                                />
                            </Feature>
                            <Feature name="PaypalResolutionCenter" accountSlug={accountSlug}>
                                <Permission name="partner_admin" accountSlug={accountSlug}>
                                    <MenuItem
                                        count={status.currentData?.openDisputeCount ?? 0}
                                        path={`/account/${accountSlug}/orders/paypal/disputes/active`}
                                        text={t('sidebar.menu.orderDisputes')}
                                        onClick={logClick}
                                    />
                                </Permission>
                            </Feature>
                            <Feature name="openCartView" accountSlug={accountSlug}>
                                <MenuItem
                                    path={pathUtils.populateParams(ORDER_PATHS.fullPaths.cartPage, { accountSlug })}
                                    text={isNewCartPageEnabled ? t('sidebar.menu.pendingReservations') : t('sidebar.menu.carts')}
                                    onClick={logClick}
                                />
                            </Feature>
                            {showChannelsManagerPage && (
                                <Permission name="partner_admin" accountSlug={accountSlug}>
                                    <MenuItem
                                        path={`/account/${accountSlug}/orders/channels/`}
                                        text={t('sidebar.menu.channels')}
                                        onClick={logClick}
                                    />
                                </Permission>
                            )}
                            {!isReseller && (
                                <Feature name="is_capacity_report_enabled" accountSlug={accountSlug}>
                                    <MenuItem
                                        path={`/account/${accountSlug}/orders/capacity_report/`}
                                        text={t('sidebar.menu.capacity')}
                                        onClick={logClick}
                                    />
                                </Feature>
                            )}
                        </MenuItem>
                    </Permission>
                    {showProducts && (
                        <MenuItem
                            path={`/account/${accountSlug}/products`}
                            text={t('sidebar.menu.products')}
                            iconName="Tickets"
                            onClick={setOpenItem}
                            close={openItem !== `/account/${accountSlug}/products`}
                        >
                            <MenuItem
                                path={`/account/${accountSlug}/products/home/nested`}
                                text={t('sidebar.menu.products')}
                                onClick={logClick}
                            />
                            <Feature name="productsBeta" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/products/productTable`}
                                    text={t('sidebar.menu.products')}
                                    floatText="BETA"
                                    onClick={logClick}
                                />
                            </Feature>
                            <Feature name="Pricing" accountSlug={accountSlug}>
                                <Permission name="view_pricing_settings" accountSlug={accountSlug}>
                                    <MenuItem
                                        path={`/account/${accountSlug}/products/pricing/exceptions/list`}
                                        text={t('sidebar.menu.pricingExceptions')}
                                        onClick={logClick}
                                    />
                                </Permission>
                            </Feature>
                            <Permission name="view_product_validity" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/products/validity/exceptions/list`}
                                    text={t('sidebar.menu.validityExceptions')}
                                    onClick={logClick}
                                />
                            </Permission>
                            <Permission name="partner_admin" accountSlug={accountSlug}>
                                <Feature name="CapacitySection" accountSlug={accountSlug}>
                                    <MenuItem
                                        path={`/account/${accountSlug}/products/inventory/home/`}
                                        text={t('sidebar.menu.capacity')}
                                        onClick={logClick}
                                    />
                                </Feature>
                                <Feature name="CapacitySection" accountSlug={accountSlug} invert>
                                    <MenuItem
                                        path={`/account/${accountSlug}/products/inventory/home/`}
                                        text={t('sidebar.menu.crowdControl')}
                                        onClick={logClick}
                                    />
                                </Feature>
                                <MenuItem
                                    path={`/account/${accountSlug}/products/time_slots/home/`}
                                    text={t('sidebar.menu.timeSlots')}
                                    onClick={logClick}
                                />
                                <MenuItem
                                    path={`/account/${accountSlug}/products/apf_studio/`}
                                    text={t('sidebar.menu.apfStudio')}
                                    onClick={logClick}
                                />
                                <Feature name="DiscountRulesPage" accountSlug={accountSlug}>
                                    <MenuItem
                                        path={`/account/${accountSlug}/products/discount_rules/home/`}
                                        text={t('sidebar.menu.discountRules')}
                                        onClick={logClick}
                                    />
                                </Feature>
                            </Permission>
                        </MenuItem>
                    )}
                    {!isReseller && (
                        <MenuItem
                            path={`/account/${accountSlug}/venue/`}
                            text={t('sidebar.menu.venue')}
                            iconName="MapPinned"
                            onClick={setOpenItem}
                            close={openItem !== `/account/${accountSlug}/venue/`}
                        >
                            <Permission name="partner_admin" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/venue/opening_times/`}
                                    text={t('sidebar.menu.openingTimes')}
                                    onClick={logClick}
                                />
                            </Permission>
                            <Permission name="access_codes" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/venue/codes`}
                                    text={t('sidebar.menu.codes')}
                                    onClick={logClick}
                                />
                            </Permission>
                            <Feature name="parkMap" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/venue/park_map`}
                                    text={t('sidebar.menu.parkMap')}
                                    onClick={logClick}
                                />
                            </Feature>
                        </MenuItem>
                    )}
                    {!isReseller && (
                        <MenuItem
                            path={`/account/${accountSlug}/pos/`}
                            text={t('sidebar.menu.pointOfSale')}
                            iconName="Monitor"
                            onClick={clickChildless}
                            floatText="NEW"
                        />
                    )}
                    {!isReseller && (
                        <Feature name="memberships" accountSlug={accountSlug}>
                            <Permission name="access_orders" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/memberships/`}
                                    className="memberships"
                                    text={t('sidebar.menu.memberships')}
                                    iconName="IdCard"
                                    onClick={logClick}
                                />
                            </Permission>
                        </Feature>
                    )}
                    <Feature name="pricingMenu" accountSlug={accountSlug}>
                        <Permission name="partner_admin" accountSlug={accountSlug}>
                            <MenuItem
                                path={`/account/${accountSlug}/global_pricing_settings`}
                                text={t('sidebar.menu.dynamicPricing')}
                                iconName="Sparkles"
                                onClick={logClick}
                            />
                        </Permission>
                    </Feature>
                    <Feature name="discountCodes" accountSlug={accountSlug}>
                        <Permission name="access_codes" accountSlug={accountSlug}>
                            <MenuItem
                                path={`/account/${accountSlug}/discounts/pools`}
                                className="discounts"
                                text={t('sidebar.menu.discounts')}
                                iconName="BadgePercent"
                                onClick={logClick}
                            />
                        </Permission>
                    </Feature>
                    <Permission name="view_wonderbar" accountSlug={accountSlug}>
                        <MenuItem
                            path={`/account/${accountSlug}/engage/`}
                            text={t('sidebar.menu.engage')}
                            iconName="RadioTower"
                            onClick={setOpenItem}
                            close={openItem !== `/account/${accountSlug}/engage/`}
                        >
                            <MenuItem
                                path={`/account/${accountSlug}/engage/tools/home/checkout`}
                                text={t('sidebar.menu.engageTools')}
                                onClick={logClick}
                            />
                            <Permission name="partner_admin" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/engage/copy_editor/home/`}
                                    text={t('sidebar.menu.copyEditor')}
                                    onClick={logClick}
                                />
                                <MenuItem
                                    path={`/account/${accountSlug}/engage/email_templates/home/`}
                                    text={t('sidebar.menu.emailTemplates')}
                                    onClick={logClick}
                                />
                                <MenuItem
                                    path={`/account/${accountSlug}/engage/ticket_templates/`}
                                    text={t('sidebar.menu.ticketTemplates')}
                                    onClick={logClick}
                                />
                                <MenuItem
                                    path={`/account/${accountSlug}/engage/gift_card_templates/`}
                                    text={t('sidebar.menu.giftCardTemplates')}
                                    onClick={logClick}
                                />
                            </Permission>
                        </MenuItem>
                    </Permission>
                    <Permission name="access_analytics" accountSlug={accountSlug}>
                        <MenuItem
                            path={`/account/${accountSlug}/reports/`}
                            text={t('sidebar.menu.reports')}
                            iconName="ChartLine"
                            onClick={setOpenItem}
                            close={openItem !== `/account/${accountSlug}/reports/`}
                        >
                            <Feature name="ReportsOverview" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/reports/overview/`}
                                    text={t('sidebar.menu.overview')}
                                    onClick={logClick}
                                />
                            </Feature>
                            <Feature name="sale_reports" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/reports/sales-old`}
                                    text={t('sidebar.menu.salesOverview')}
                                    userpilot="menu-sales"
                                    onClick={logClick}
                                />
                            </Feature>
                            {supersetReports.map((report) => (
                                <MenuItem
                                    key={report.dashboardId}
                                    path={`/account/${accountSlug}/reports/superset/${report.dashboardId}/`}
                                    text={report.menuTitle}
                                    onClick={logClick}
                                />
                            ))}
                            <Feature name="SalesAndFinanceReport" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/reports/sales`}
                                    text={t('sidebar.menu.salesAndFinance')}
                                    onClick={logClick}
                                />
                            </Feature>
                            <Feature name="SalesAndFinanceReportDemo" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/reports/sales-demo`}
                                    text={t('sidebar.menu.salesAndFinanceDemo')}
                                    onClick={logClick}
                                />
                            </Feature>
                            <Feature name="OperationsReport" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/reports/operations`}
                                    text={t('sidebar.menu.operations')}
                                    onClick={logClick}
                                />
                            </Feature>
                            <Feature name="MarketingReport" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/reports/marketing`}
                                    text={t('sidebar.menu.marketing')}
                                    onClick={logClick}
                                    floatText="NEW"
                                />
                            </Feature>
                            <Feature name="TrafficReport" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/reports/traffic/`}
                                    text={t('sidebar.menu.traffic')}
                                    userpilot="menu-traffic"
                                    onClick={logClick}
                                />
                            </Feature>
                            <Feature name="CheckoutFunnelReport" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/reports/checkout_funnel/`}
                                    text={t('sidebar.menu.checkoutFunnel')}
                                    onClick={logClick}
                                />
                            </Feature>
                            <Feature name="EmailsReport" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/reports/emails/`}
                                    text={t('sidebar.menu.emails')}
                                    onClick={logClick}
                                />
                            </Feature>
                            <Feature name="BarcodesReport" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/reports/barcodes/`}
                                    text={t('sidebar.menu.barcodes')}
                                    onClick={logClick}
                                />
                            </Feature>
                            <Feature name="DiscountCodesReport" accountSlug={accountSlug}>
                                <MenuItem
                                    path={`/account/${accountSlug}/reports/discount_codes/`}
                                    text={t('sidebar.menu.discountCodes')}
                                    onClick={logClick}
                                />
                            </Feature>
                            <Feature name="CRMReportPage" accountSlug={accountSlug}>
                                <MenuItem path={`/account/${accountSlug}/reports/crm/`} text={t('sidebar.menu.crm')} onClick={logClick} />
                            </Feature>
                            <Permission name="view_wonderbar" accountSlug={accountSlug}>
                                <Feature name="EngageToolsReport" accountSlug={accountSlug}>
                                    <MenuItem
                                        path={`/account/${accountSlug}/reports/engage_tools/`}
                                        text={t('sidebar.menu.engageTools')}
                                        onClick={logClick}
                                    />
                                </Feature>
                            </Permission>
                        </MenuItem>
                    </Permission>
                    {!isReseller && (
                        <Feature name="release_notes_page" accountSlug={accountSlug}>
                            <MenuItem
                                path={`/account/${accountSlug}/release_notes/`}
                                text={t('sidebar.menu.whatsNew')}
                                onClick={logClick}
                                iconName="Mailbox"
                                rightComponent={<div dangerouslySetInnerHTML={{ __html: RELEASED_BADGE }} />}
                            />
                        </Feature>
                    )}
                    <Permission name="partner_admin" accountSlug={accountSlug}>
                        <MenuItem
                            path={`/account/${accountSlug}/settings`}
                            text={t('sidebar.menu.settings')}
                            iconName="Settings2"
                            onClick={setOpenItem}
                            close={openItem !== `/account/${accountSlug}/settings`}
                        >
                            <MenuItem
                                path={`/account/${accountSlug}/settings/account_settings/`}
                                text={t('sidebar.menu.account')}
                                onClick={logClick}
                            />
                            <MenuItem
                                path={`/account/${accountSlug}/settings/add_snippet/`}
                                text={t('sidebar.menu.script')}
                                onClick={logClick}
                            />
                            <MenuItem
                                path={`/account/${accountSlug}/settings/users/`}
                                text={t('sidebar.menu.users')}
                                onClick={logClick}
                            />
                        </MenuItem>
                    </Permission>
                    <AdminRequired>
                        <MenuItem
                            path="/admin/"
                            text={t('sidebar.menu.admin')}
                            iconName="Settings"
                            onClick={setOpenItem}
                            close={openItem !== '/admin/'}
                        >
                            <MenuItem path="/admin/onboarding/" text={t('sidebar.menu.onboarding')} onClick={logClick} />
                            <Permission name="fund_transfers" accountSlug={accountSlug}>
                                <MenuItem path="/admin/fund_transfer/" text={t('sidebar.menu.fundTransfer')} onClick={logClick} />
                            </Permission>
                        </MenuItem>
                    </AdminRequired>
                </ul>
                <ul className="profile-menu">
                    <LanguageSelector />
                    {!isReseller && (
                        <MenuItem
                            path={`/account/${accountSlug}/notifications/feed`}
                            className={classNames({ notifications: true, bubble: newNotificationsCount > 0 })}
                            text={t('sidebar.menu.notifications')}
                            iconName="Bell"
                            onClick={logClick}
                        />
                    )}
                    <MenuItem
                        path="/personal_settings/personal"
                        className={classNames({ profile: true, bubble: profile && !profile.emailConfirmed })}
                        text={t('sidebar.menu.profile')}
                        iconName={profile?.avatarUrl ? undefined : 'UserPen'}
                        image={profile?.avatarUrl}
                        onClick={logClick}
                    />
                    <MenuItem className="logout" path="" text={t('sidebar.menu.logout')} iconName="LogOut" onClick={logout} />
                </ul>
            </nav>
            <div className="app-version" data-version={process.env.VERSION} title={process.env.VERSION}>
                {process.env.VERSION}
            </div>
        </div>
    )
}
